import toNumber from 'lodash/toNumber'
import type { File } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, Icon } from '@ztechapps/webui.util'
import type { SupportedMimeType } from './AttachFileButton'
import styles from './styles.module.css'

export type FileAttachmentPreviewProps = {
  /** The uploaded file to display */
  file: File
  /** MIME type of the file */
  mimeType?: SupportedMimeType
  /** Callback when the remove button is clicked */
  onRemove: () => void
  /** Whether the remove button should be disabled */
  disabled?: boolean
}

/**
 * Formats a file size in bytes to a human-readable string.
 */
const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${toNumber((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

/**
 * Determines if the MIME type represents an image.
 */
const isImageMimeType = (mimeType?: string): boolean => {
  return mimeType?.startsWith('image/') ?? false
}

/**
 * Gets the appropriate icon name for the file type.
 */
const getFileIcon = (mimeType?: string): string => {
  if (mimeType === 'application/pdf') {
    return 'file-pdf'
  }
  if (isImageMimeType(mimeType)) {
    return 'image'
  }
  return 'file'
}

/**
 * Gets the appropriate icon color class for the file type.
 */
const getFileIconClass = (mimeType?: string): string => {
  if (mimeType === 'application/pdf') {
    return 'text-danger'
  }
  if (isImageMimeType(mimeType)) {
    return styles.imageIcon
  }
  return 'text-secondary'
}

/**
 * Gets a user-friendly description of the file type.
 */
const getFileTypeLabel = (mimeType?: string): string => {
  if (mimeType === 'application/pdf') {
    return 'PDF'
  }
  if (mimeType === 'image/png') {
    return 'PNG'
  }
  if (mimeType === 'image/jpeg') {
    return 'JPEG'
  }
  if (mimeType === 'image/gif') {
    return 'GIF'
  }
  if (mimeType === 'image/webp') {
    return 'WebP'
  }
  return 'File'
}

/**
 * FileAttachmentPreview component displays a preview of a pending file attachment.
 * Shows the filename, size, file type indicator, and a remove button.
 * For images, displays a thumbnail preview; for PDFs, shows a PDF icon.
 *
 * @example
 * ```tsx
 * <FileAttachmentPreview
 *   file={attachedFile}
 *   mimeType="image/png"
 *   onRemove={() => setAttachedFile(null)}
 * />
 * ```
 */
export const FileAttachmentPreview = ({ file, mimeType, onRemove, disabled }: FileAttachmentPreviewProps) => {
  const isImage = isImageMimeType(mimeType)
  const iconName = getFileIcon(mimeType)
  const iconClass = getFileIconClass(mimeType)
  const fileTypeLabel = getFileTypeLabel(mimeType)

  return (
    <HorizontalStack gap={2} className="p-2 bg-secondary-subtle rounded align-items-center">
      {/* Image thumbnail or file icon */}
      {isImage && file.uri ? (
        <img
          src={file.uri}
          alt={file.name ?? 'Attached image'}
          style={{
            width: 40,
            height: 40,
            objectFit: 'cover',
            borderRadius: 4,
          }}
        />
      ) : (
        <Icon name={iconName} className={`${iconClass} fs-5`} />
      )}

      {/* File info */}
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {file.name ?? `Attached ${fileTypeLabel}`}
        </div>
        <div className="text-muted small">
          {fileTypeLabel} &middot; {formatFileSize(file.size)}
        </div>
      </div>

      {/* Remove button */}
      <BusyButton
        variant="link"
        onClick={onRemove}
        disabled={disabled}
        icon="x"
        text=""
        title="Remove attachment"
        className="text-secondary p-0"
      />
    </HorizontalStack>
  )
}
